import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";

export interface ColumnDefinition {
  id: string;
  label: string;
  isDefault: boolean;
  category: "default" | "additional";
}

// Define all available columns
export const ALL_COLUMNS: ColumnDefinition[] = [
  // Default columns (currently visible in the table)
  { id: "accountNumber", label: "Account #", isDefault: true, category: "default" },
  { id: "name", label: "Name", isDefault: true, category: "default" },
  { id: "currency", label: "Currency", isDefault: true, category: "default" },
  { id: "outstanding", label: "Outstanding", isDefault: true, category: "default" },
  { id: "overdue", label: "Overdue", isDefault: true, category: "default" },
  { id: "creditLimit", label: "Credit Limit", isDefault: true, category: "default" },
  { id: "dso", label: "DSO", isDefault: true, category: "default" },
  { id: "risk", label: "Risk", isDefault: true, category: "default" },
  { id: "lastPayment", label: "Last Payment", isDefault: true, category: "default" },
  { id: "collectionStrategy", label: "Collection Strategy", isDefault: true, category: "default" },
  
  // Additional columns (not currently visible by default)
  { id: "contact", label: "Contact Email", isDefault: false, category: "additional" },
  { id: "phone", label: "Phone", isDefault: false, category: "additional" },
  { id: "address", label: "Address", isDefault: false, category: "additional" },
  { id: "paymentTerms", label: "Payment Terms", isDefault: false, category: "additional" },
  { id: "creditController", label: "Credit Controller", isDefault: false, category: "additional" },
  { id: "accountManager", label: "Account Manager", isDefault: false, category: "additional" },
  { id: "availableCredit", label: "Available Credit", isDefault: false, category: "additional" },
  { id: "creditUtilization", label: "Credit Utilisation %", isDefault: false, category: "additional" },
];

export const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.filter(col => col.isDefault).map(col => col.id);

interface ColumnConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visibleColumns: string[];
  onColumnsChange: (columns: string[]) => void;
}

export function ColumnConfigDialog({
  open,
  onOpenChange,
  visibleColumns,
  onColumnsChange,
}: ColumnConfigDialogProps) {
  const [selectedColumns, setSelectedColumns] = useState<string[]>(visibleColumns);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Update local state when visibleColumns prop changes
  useEffect(() => {
    setSelectedColumns(visibleColumns);
  }, [visibleColumns]);

  const handleToggleColumn = (columnId: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnId)) {
        // Don't allow removing all columns
        if (prev.length === 1) {
          toast.error("At least one column must be visible");
          return prev;
        }
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(ALL_COLUMNS.map((col) => col.id));
  };

  const handleDeselectAll = () => {
    // Keep at least one column selected (accountNumber)
    setSelectedColumns(["accountNumber"]);
    toast.info("At least one column must remain visible");
  };

  const handleResetToDefault = () => {
    setSelectedColumns(DEFAULT_VISIBLE_COLUMNS);
    toast.success("Reset to default columns");
  };

  const handleApply = () => {
    onColumnsChange(selectedColumns);
    onOpenChange(false);
    toast.success("Column configuration updated");
  };

  const handleCancel = () => {
    setSelectedColumns(visibleColumns);
    onOpenChange(false);
  };

  // Drag and drop handlers
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newColumns = [...selectedColumns];
    const [draggedColumn] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(dropIndex, 0, draggedColumn);
    
    setSelectedColumns(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Get ordered columns with their metadata
  const orderedColumns = selectedColumns
    .map(id => ALL_COLUMNS.find(col => col.id === id))
    .filter((col): col is ColumnDefinition => col !== undefined);

  const defaultColumns = ALL_COLUMNS.filter((col) => col.category === "default");
  const additionalColumns = ALL_COLUMNS.filter((col) => col.category === "additional");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogDescription>
            Select which columns to display and drag to reorder them. Your preferences will be saved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAll}
            >
              Select All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDeselectAll}
            >
              Deselect All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetToDefault}
            >
              Reset to Default
            </Button>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-6">
            {/* Left Column: Column Selection */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground">
                Available Columns
              </h3>
              <ScrollArea className="h-[400px]">
                <ScrollAreaViewport className="h-full">
                  <div className="space-y-6 pr-4">
                    {/* Default Columns Section */}
                    <div>
                      <h4 className="text-xs font-medium mb-3 text-muted-foreground uppercase">
                        Default Columns
                      </h4>
                      <div className="space-y-2">
                        {defaultColumns.map((column) => (
                          <div 
                            key={column.id} 
                            className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                          >
                            <Checkbox
                              id={column.id}
                              checked={selectedColumns.includes(column.id)}
                              onCheckedChange={() => handleToggleColumn(column.id)}
                            />
                            <Label
                              htmlFor={column.id}
                              className="text-sm font-medium leading-none cursor-pointer flex-1"
                            >
                              {column.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Separator />

                    {/* Additional Columns Section */}
                    <div>
                      <h4 className="text-xs font-medium mb-3 text-muted-foreground uppercase">
                        Additional Columns
                      </h4>
                      <div className="space-y-2">
                        {additionalColumns.map((column) => (
                          <div 
                            key={column.id} 
                            className="flex items-center space-x-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                          >
                            <Checkbox
                              id={column.id}
                              checked={selectedColumns.includes(column.id)}
                              onCheckedChange={() => handleToggleColumn(column.id)}
                            />
                            <Label
                              htmlFor={column.id}
                              className="text-sm font-medium leading-none cursor-pointer flex-1"
                            >
                              {column.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>

            {/* Right Column: Column Order */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Column Order
                </h3>
                <span className="text-xs text-muted-foreground">
                  Drag to reorder
                </span>
              </div>
              <ScrollArea className="h-[400px]">
                <ScrollAreaViewport className="h-full">
                  {orderedColumns.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
                      No columns selected
                    </div>
                  ) : (
                    <div className="space-y-2 pr-4">
                      {orderedColumns.map((column, index) => (
                        <div
                          key={column.id}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`
                            flex items-center gap-3 p-3 rounded-md border bg-card
                            cursor-move transition-all
                            ${draggedIndex === index ? 'opacity-50' : 'opacity-100'}
                            ${dragOverIndex === index && draggedIndex !== index ? 'border-primary border-2' : 'border-border'}
                            hover:bg-muted/50 hover:shadow-sm
                          `}
                        >
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono text-muted-foreground w-6">
                                {index + 1}
                              </span>
                              <span className="text-sm font-medium truncate">
                                {column.label}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleToggleColumn(column.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
                            title="Remove column"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollAreaViewport>
                <ScrollAreaScrollbar orientation="vertical" />
              </ScrollArea>
            </div>
          </div>

          <Separator />

          {/* Summary */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {selectedColumns.length} of {ALL_COLUMNS.length} columns selected
            </span>
            {orderedColumns.length > 0 && (
              <span className="text-xs">
                Columns will appear in the order shown on the right
              </span>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
